import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Link,
} from "@mui/material";
import { Model } from "survey-core";

import { useSurveyAccess } from "../hooks/useSurveyAccess";
import { useSurveyResponses } from "../hooks/useSurveyResponses";
import { friendlyLabel } from "../hooks/useNetworkData";

const ValueChips = ({ values }: { values: string[] }) => {
  if (!values.length) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {values.map((val, idx) => (
        <Chip key={`${val}-${idx}`} label={val} size="small" sx={{ mb: 0.5 }} />
      ))}
    </Stack>
  );
};

const formatValue = (val: any) => {
  if (val === null || val === undefined || val === "") return "—";
  if (Array.isArray(val)) {
    const isPrimitive = val.every(
      (item) =>
        item === null ||
        item === undefined ||
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean"
    );
    if (isPrimitive) {
      return val.map((item) => (item === null || item === undefined ? "" : String(item))).join(", ");
    }
    return JSON.stringify(val, null, 2);
  }
  if (typeof val === "object") return JSON.stringify(val, null, 2);
  return String(val);
};

export default function AnalyticsDebug() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { loading, allowed, survey } = useSurveyAccess(id!, "analytics");
  const { responses, loading: respLoading, error: respError } = useSurveyResponses(id, !loading && allowed);

  const questions = useMemo(() => {
    if (!survey?.json) return [];
    const surveyModel = new Model(survey.json);
    return surveyModel.getAllQuestions().map((q: any) => ({
      name: q.name,
      title: q.title || friendlyLabel(q.name),
    }));
  }, [survey]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.key || "").toLowerCase() !== "d") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const focusEl = e.target as HTMLElement | null;
      if (focusEl) {
        const tag = focusEl.tagName;
        const isFormField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || focusEl.isContentEditable;
        if (isFormField) return;
      }
      if (id) navigate(`/analytics/${id}`);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [id, navigate]);

  if (loading || respLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box p={4}>
        <Typography color="error">You don’t have access to this survey.</Typography>
      </Box>
    );
  }

  if (respError) {
    return (
      <Box p={4}>
        <Typography color="error">{respError}</Typography>
      </Box>
    );
  }

  const surveyTitle = survey?.json?.title || id;

  return (
    <Box sx={{ p: 3, pb: 6 }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Respondent Detail Debug
        </Typography>
        <Link component={RouterLink} to={`/analytics/${id}`} underline="hover">
          ← Back to Analytics
        </Link>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Survey: <strong>{surveyTitle}</strong>
      </Typography>

      <Stack spacing={3}>
        {responses.map((resp: any, idx: number) => {
          const meta = resp.__meta || {};
          return (
            <Paper key={resp.id} elevation={1} sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h6">Response #{idx + 1}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Submitted: {formatDateTime(meta.submittedAt)}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                {questions.map((q) => {
                  const value = resp[q.name];
                  const renderValue = () => {
                    if (Array.isArray(value)) {
                      const isPrimitiveArray = value.every(
                        (item) =>
                          item === null ||
                          item === undefined ||
                          typeof item === "string" ||
                          typeof item === "number" ||
                          typeof item === "boolean"
                      );
                      if (isPrimitiveArray) {
                        return (
                          <ValueChips
                            values={value.map((item) =>
                              item === null || item === undefined ? "" : String(item)
                            )}
                          />
                        );
                      }
                      return (
                        <Box
                          component="pre"
                          sx={{
                            fontSize: 12,
                            backgroundColor: "#f7f7f7",
                            p: 1,
                            borderRadius: 1,
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {formatValue(value)}
                        </Box>
                      );
                    }
                    if (typeof value === "object") {
                      return (
                        <Box
                          component="pre"
                          sx={{
                            fontSize: 12,
                            backgroundColor: "#f7f7f7",
                            p: 1,
                            borderRadius: 1,
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {formatValue(value)}
                        </Box>
                      );
                    }
                    return <Typography variant="body2">{formatValue(value)}</Typography>;
                  };

                  return (
                    <Box key={`${resp.id}-${q.name}`}>
                      <Typography variant="caption" color="text.secondary">
                        {q.title}
                      </Typography>
                      {renderValue()}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
const formatDateTime = (value: any) => {
  if (!value) return "—";
  try {
    const date =
      typeof value === "object" && typeof value.toDate === "function"
        ? value.toDate()
        : value instanceof Date
        ? value
        : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
  } catch {
    return "—";
  }
};
